import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import axios from 'axios'

import { apiErrorMessage } from '@/api/client'
import { languages as languagesApi, settings as api } from '@/api/endpoints'
import { Alert, Checkbox, Field, Input, Select, Spinner, Textarea } from '@/components/ui'
import MultiSelect from '@/components/ui/MultiSelect'
import BankAccounts from '@/features/centers/BankAccounts'
import { PlanLock, usePlanFeature } from '@/features/billing/PlanFeature'
import { useBootstrap } from '@/hooks/useBootstrap'
import { LANGUAGES } from '@/i18n'
import { getActiveLocale, languageName } from '@/lib/utils'
import type { Settings } from '@/types/api'

/**
 * Poradie ponuky jazykov v dropdowne - najprv tie, ktoré si školy vyberajú
 * najčastejšie (susedné krajiny a bežné turistické jazyky), zvyšok abecedne
 * podľa názvu. Zoznam sa časom rozrastá (`sql/019_more_languages.sql`
 * a ďalšie), preto nejde o poradie podľa `id`.
 */
const LANGUAGE_PRIORITY = ['SK', 'EN', 'DE', 'PL', 'CS', 'HU', 'IT', 'RU', 'FR', 'UK']

function languageSort(a: { key: string | null; name: string | null }, b: { key: string | null; name: string | null }) {
  const ai = LANGUAGE_PRIORITY.indexOf(a.key ?? '')
  const bi = LANGUAGE_PRIORITY.indexOf(b.key ?? '')
  if (ai !== -1 || bi !== -1) return (ai === -1 ? LANGUAGE_PRIORITY.length : ai) - (bi === -1 ? LANGUAGE_PRIORITY.length : bi)
  return (a.name ?? '').localeCompare(b.name ?? '')
}

/** SettingsPresenter::actionSystem */
export default function SystemTab() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['settings'], queryFn: api.get })
  const { data: boot } = useBootstrap()
  // Zamknutý modul sa nedá zapnúť; ten, čo už zapnutý je, sa dá vypnúť.
  const cards = usePlanFeature('card_payments').allowed
  const qr = usePlanFeature('qr_payment').allowed
  const { from: emailBrandFrom, allowed: emailBrand } = usePlanFeature('email_brand')
  const messenger = usePlanFeature('messenger').allowed
  const emails = usePlanFeature('email_notifications')

  const [form, setForm] = useState<Partial<Settings>>({})
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  // Chyby jednotlivých polí zo servera - aby bolo vidno, ktoré políčko,
  // a nie len hlášku hore nad dlhým formulárom.
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (data) setForm(data)
  }, [data])

  const save = useMutation({
    mutationFn: (payload: Partial<Settings>) => api.update(payload),
    onSuccess: () => {
      setMessage({ kind: 'success', text: t('global.saved') })
      setFieldErrors({})
      void qc.invalidateQueries({ queryKey: ['settings'] })
      void qc.invalidateQueries({ queryKey: ['bootstrap'] })
    },
    onError: (err) => {
      setMessage({ kind: 'error', text: apiErrorMessage(err) })
      const body = axios.isAxiosError(err) ? err.response?.data : null
      const errors: Record<string, string> = {}
      if (body && typeof body === 'object') {
        for (const [field, value] of Object.entries(body as Record<string, unknown>)) {
          const first = Array.isArray(value) ? value[0] : value
          if (typeof first === 'string') errors[field] = first
        }
      }
      setFieldErrors(errors)
    },
  })

  const uploadLogo = useMutation({
    mutationFn: (file: File) => api.uploadLogo(file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings'] })
      void qc.invalidateQueries({ queryKey: ['bootstrap'] })
    },
  })

  if (isLoading || !data) {
    return (
      <div className="flex h-40 items-center justify-center text-slate-400">
        <Spinner className="h-5 w-5" />
      </div>
    )
  }

  function set<K extends keyof Settings>(key: K, value: Settings[K]) {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <form
      className="max-w-3xl space-y-5"
      onSubmit={(e) => {
        e.preventDefault()
        setMessage(null)
        setFieldErrors({})
        // IBAN QR platby spravujú účty (`BankAccounts`) - starú hodnotu
        // z formulára neposielať, prepísala by predvolený účet.
        const { qr_iban: _iban, ...payload } = form
        save.mutate(payload)
      }}
    >
      {message ? <Alert variant={message.kind}>{message.text}</Alert> : null}

      <section className="card space-y-3 p-4">
        <div className="grid gap-2 sm:grid-cols-2">
          <Field label={t('settings.companyName')}>
            <Input
              value={form.company_name ?? ''}
              onChange={(e) => set('company_name', e.target.value)}
            />
          </Field>
          <Field label={t('settings.systemName')}>
            <Input
              value={form.system_name ?? ''}
              onChange={(e) => set('system_name', e.target.value)}
            />
          </Field>
        </div>

        <Field label={t('settings.logo')}>
          <div className="flex items-center gap-3">
            {data.own_logo || data.logo_url ? (
              <img src={data.own_logo || data.logo_url} alt="" className="h-10 w-10 rounded object-contain" />
            ) : null}
            <input
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              className="text-sm"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) uploadLogo.mutate(file)
              }}
            />
            {uploadLogo.isPending ? <Spinner /> : null}
          </div>
          {/* Logo školy (aplikácia, prihlásenie, widget, e-maily) je od Plus
              (`plans.EMAIL_BRAND`) - dovtedy všade logo Learn2Ski. */}
          <PlanLock feature="email_brand" text={t('settings.logoEmailLocked', { plan: emailBrandFrom })} />
        </Field>

        {/* Vlastný kontakt v pätičke e-mailov a Reply-To - rovnako ako logo
            od balíčka s vlastným brandom (`Settings.brand_contact_email`). */}
        <Field label={t('settings.contactEmail')} error={fieldErrors.contact_email}>
          <Input
            type="email"
            value={form.contact_email ?? ''}
            disabled={!emailBrand}
            placeholder="info@vasaskola.sk"
            onChange={(e) => set('contact_email', e.target.value)}
          />
          {emailBrand ? (
            <p className="mt-1 text-xs text-slate-500">{t('settings.contactEmailHelp')}</p>
          ) : (
            <PlanLock feature="email_brand" text={t('settings.contactEmailLocked', { plan: emailBrandFrom })} />
          )}
        </Field>
      </section>

      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold">{t('nav.calendar')}</h2>
        <div className="grid gap-2 sm:grid-cols-4">
          <Field label={t('settings.startHour')}>
            <Input
              type="number"
              min={0}
              max={23}
              step={0.5}
              value={form.start_hour ?? 9}
              onChange={(e) => set('start_hour', Number.parseFloat(e.target.value))}
            />
          </Field>
          <Field label={t('settings.endHour')}>
            <Input
              type="number"
              min={1}
              max={24}
              step={0.5}
              value={form.end_hour ?? 16}
              onChange={(e) => set('end_hour', Number.parseFloat(e.target.value))}
            />
          </Field>
          <Field label={t('settings.partsInHour')}>
            <Select
              value={form.parts_in_hour ?? 2}
              onChange={(e) => set('parts_in_hour', Number.parseInt(e.target.value, 10))}
            >
              <option value={1}>60 min</option>
              <option value={2}>30 min</option>
              <option value={3}>20 min</option>
              <option value={4}>15 min</option>
            </Select>
          </Field>
          <Field label={t('settings.maxClients')}>
            <Input
              type="number"
              min={1}
              max={20}
              value={form.max_clients_per_reservation ?? 5}
              onChange={(e) =>
                set('max_clients_per_reservation', Number.parseInt(e.target.value, 10))
              }
            />
          </Field>
        </div>

        <div className="grid gap-2 sm:grid-cols-2">
          <Field label={t('settings.language')}>
            <Select value={form.lang ?? 'sk'} onChange={(e) => set('lang', e.target.value)}>
              {LANGUAGES.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('settings.currency')} error={undefined}>
            <Select
              value={form.currency_id ?? 1}
              onChange={(e) => set('currency_id', Number.parseInt(e.target.value, 10))}
            >
              {(boot?.currencies ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {currencyLabel(c.key)} — {c.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <p className="text-xs text-slate-500">{t('settings.currencyHint')}</p>
      </section>

      <LanguagesSection />

      <section className="card space-y-2 p-4">
        <h2 className="text-sm font-semibold">{t('settings.modules')}</h2>
        <Checkbox
          label={t('settings.modulePricing')}
          checked={form.module_pricing ?? true}
          onChange={(e) => set('module_pricing', e.target.checked)}
        />
        <Checkbox
          label={t('settings.moduleCashReceipt')}
          checked={form.module_cash_receipt ?? false}
          onChange={(e) => set('module_cash_receipt', e.target.checked)}
        />
        {/* Plná evidencia (sumy, zálohy, vrátenia, kredit) alebo len označenie
            úhrady pre školy, ktoré platby vedú v inom systéme. */}
        <fieldset className="space-y-1 rounded border border-slate-200 p-2.5">
          <legend className="px-1 text-xs font-medium uppercase text-slate-500">
            {t('settings.paymentTracking')}
          </legend>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              className="mt-1"
              checked={!form.simple_payments}
              onChange={() => set('simple_payments', false)}
            />
            <span>
              {t('settings.paymentsFull')}
              <span className="block text-xs text-slate-500">{t('settings.paymentsFullHint')}</span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input
              type="radio"
              className="mt-1"
              checked={Boolean(form.simple_payments)}
              onChange={() => set('simple_payments', true)}
            />
            <span>
              {t('settings.paymentsSimple')}
              <span className="block text-xs text-slate-500">{t('settings.paymentsSimpleHint')}</span>
            </span>
          </label>
        </fieldset>
        <Checkbox
          label={t('settings.moduleStripe')}
          checked={form.module_stripe ?? false}
          disabled={!cards && !form.module_stripe}
          onChange={(e) => set('module_stripe', e.target.checked)}
        />
        <PlanLock feature="card_payments" />

        {/* Kľúče sa nikdy nevracajú z API — do polí sa len zapisuje. Prázdne
            pole preto neznamená „vymazať“, ale „nechať tak“; že kľúč
            nastavený je, povie stav pod poliami. */}
        {form.module_stripe ? (
          <div className="mt-2 space-y-2 rounded-md border border-slate-200 p-3">
            <p className="text-xs text-slate-500">{t('settings.stripeHint')}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label={t('settings.stripeSecretKey')}>
                <Input
                  type="password"
                  autoComplete="off"
                  placeholder={data?.modules?.stripe?.configured ? '••••••••' : 'sk_live_…'}
                  value={form.stripe_secret_key ?? ''}
                  onChange={(e) => set('stripe_secret_key', e.target.value)}
                />
              </Field>
              <Field label={t('settings.stripeWebhookSecret')}>
                <Input
                  type="password"
                  autoComplete="off"
                  placeholder="whsec_…"
                  value={form.stripe_webhook_secret ?? ''}
                  onChange={(e) => set('stripe_webhook_secret', e.target.value)}
                />
              </Field>
            </div>
            <p className="text-xs text-slate-500">
              {t('settings.stripeWebhookUrl')}:{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5">
                {window.location.origin}/api/webhooks/stripe
              </code>
            </p>
            <p
              className={
                data?.modules?.stripe?.configured
                  ? 'text-xs text-emerald-700'
                  : 'text-xs text-amber-700'
              }
            >
              {data?.modules?.stripe?.configured
                ? t('settings.stripeReady')
                : t('settings.stripeMissingKey')}
            </p>
          </div>
        ) : null}

        <Checkbox
          label={t('settings.moduleQr')}
          checked={form.module_qr_payment ?? false}
          disabled={!qr && !form.module_qr_payment}
          onChange={(e) => set('module_qr_payment', e.target.checked)}
        />
        <PlanLock feature="qr_payment" />

        {form.module_qr_payment ? (
          <div className="mt-2 space-y-2 rounded-md border border-slate-200 p-3">
            <p className="text-xs text-slate-500">{t('settings.qrHint')}</p>
            {/* Účty hlavného strediska - predvolený je IBAN QR platby
                (`sql/042`), ostatné sa dajú zvoliť pri rezervácii. */}
            <h3 className="text-xs font-semibold uppercase text-slate-500">{t('accounts.title')}</h3>
            <BankAccounts
              centerId={1}
              // Len stav QR platby - celé nastavenia by zahodili neuložené
              // zmeny vo formulári.
              onChanged={() =>
                void api.get().then((fresh) => setForm((f) => ({ ...f, modules: fresh.modules, qr_iban: fresh.qr_iban })))
              }
            />
            <p
              className={
                (form.modules ?? data?.modules)?.qr_payment?.configured
                  ? 'text-xs text-emerald-700'
                  : 'text-xs text-amber-700'
              }
            >
              {(form.modules ?? data?.modules)?.qr_payment?.configured
                ? t('settings.qrReady')
                : t('settings.qrMissingIban')}
            </p>
          </div>
        ) : null}
      </section>

      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold">{t('settings.receiptNumbering')}</h2>
        <div className="grid gap-2 sm:grid-cols-4">
          <Field label={t('settings.receiptPrefix')}>
            <Input
              maxLength={10}
              placeholder="PP"
              value={form.receipt_prefix ?? ''}
              onChange={(e) => set('receipt_prefix', e.target.value)}
            />
          </Field>
          <Field label={t('settings.refundPrefix')}>
            <Input
              maxLength={10}
              placeholder="VP"
              value={form.refund_prefix ?? ''}
              onChange={(e) => set('refund_prefix', e.target.value)}
            />
          </Field>
          <Field label={t('settings.receiptNumberLength')}>
            <Input
              type="number"
              min={1}
              max={10}
              value={form.receipt_number_length ?? 5}
              onChange={(e) =>
                set('receipt_number_length', Number.parseInt(e.target.value, 10) || 5)
              }
            />
          </Field>
          <div className="flex items-end pb-1.5">
            <Checkbox
              label={t('settings.receiptIncludeYear')}
              checked={form.receipt_include_year ?? true}
              onChange={(e) => set('receipt_include_year', e.target.checked)}
            />
          </div>
        </div>
        <p className="text-xs text-slate-500">
          {t('settings.receiptNumberingHint')}{' '}
          <strong className="font-medium text-slate-700">
            {(form.receipt_prefix ?? '') +
              (form.receipt_include_year ?? true ? String(new Date().getFullYear()) : '') +
              String(1).padStart(form.receipt_number_length ?? 5, '0')}
          </strong>
          {' · '}
          <strong className="font-medium text-slate-700">
            {(form.refund_prefix || 'VP') +
              (form.receipt_include_year ?? true ? String(new Date().getFullYear()) : '') +
              String(1).padStart(form.receipt_number_length ?? 5, '0')}
          </strong>
        </p>
      </section>

      <section className="card space-y-3 p-4">
        <h2 className="text-sm font-semibold">{t('settings.invoices')}</h2>
        <div className="grid gap-2 sm:grid-cols-4">
          <Field label={t('settings.invoicePrefix')}>
            <Input
              maxLength={10}
              placeholder="FA"
              value={form.invoice_prefix ?? ''}
              onChange={(e) => set('invoice_prefix', e.target.value)}
            />
          </Field>
          <Field label={t('settings.invoiceStornoPrefix')}>
            <Input
              maxLength={10}
              placeholder="OFA"
              value={form.invoice_storno_prefix ?? ''}
              onChange={(e) => set('invoice_storno_prefix', e.target.value)}
            />
          </Field>
          <Field label={t('settings.invoiceVatRate')} hint={t('settings.invoiceVatRateHint')}>
            <Input
              type="number"
              min={0}
              max={99}
              step="0.1"
              placeholder={t('settings.invoiceVatRateAuto')}
              value={form.invoice_vat_rate ?? ''}
              onChange={(e) => set('invoice_vat_rate', e.target.value === '' ? null : e.target.value)}
            />
          </Field>
          <Field label={t('settings.invoiceDueDays')}>
            <Input
              type="number"
              min={0}
              max={120}
              value={form.invoice_due_days ?? 14}
              onChange={(e) => set('invoice_due_days', Number.parseInt(e.target.value, 10) || 0)}
            />
          </Field>
        </div>
        <Field label={t('settings.invoiceNote')} hint={t('settings.invoiceNoteHint')}>
          <Textarea
            rows={2}
            maxLength={500}
            value={form.invoice_note ?? ''}
            onChange={(e) => set('invoice_note', e.target.value)}
          />
        </Field>
        <p className="text-xs text-slate-500">
          {t('settings.invoicesHint')}{' '}
          <strong className="font-medium text-slate-700">
            {(form.invoice_prefix ?? 'FA') +
              String(new Date().getFullYear()) +
              String(1).padStart(form.receipt_number_length ?? 5, '0')}
          </strong>
          {' · '}
          <strong className="font-medium text-slate-700">
            {(form.invoice_storno_prefix || 'OFA') +
              String(new Date().getFullYear()) +
              String(1).padStart(form.receipt_number_length ?? 5, '0')}
          </strong>
        </p>
      </section>

      <section className="card space-y-2 p-4">
        <h2 className="text-sm font-semibold">{t('settings.notifications')}</h2>
        <Checkbox
          label={t('settings.emailNotifications')}
          checked={form.email_notifications ?? false}
          disabled={!emails.allowed && !form.email_notifications}
          onChange={(e) => set('email_notifications', e.target.checked)}
        />
        {/* Štart neposiela e-maily z rezervácií vôbec - od Štandardu
            (`plans.EMAIL_NOTIFICATIONS`, stráži `mailer.enqueue`). */}
        <PlanLock
          feature="email_notifications"
          text={t('settings.emailsLocked', { plan: emails.from })}
        />
        {/* Pripomienka úhrady (`reservations.payment_reminders`) - len keď je
            čím platiť vopred a škola vedie sumy (nie „len označenie úhrady“). */}
        {form.email_notifications && !form.simple_payments ? (
          <Field
            label={t('settings.paymentReminderDays')}
            hint={t('settings.paymentReminderHint')}
            error={fieldErrors.payment_reminder_days}
          >
            <Input
              type="number"
              min={0}
              max={30}
              value={form.payment_reminder_days ?? 3}
              className="sm:max-w-[8rem]"
              onChange={(e) => set('payment_reminder_days', Number.parseInt(e.target.value, 10) || 0)}
            />
          </Field>
        ) : null}
        <Checkbox
          label={t('settings.smsNotifications')}
          checked={form.sms_notifications ?? false}
          onChange={(e) => set('sms_notifications', e.target.checked)}
        />
        <Checkbox
          label={t('settings.messengerNotifications')}
          checked={form.messenger_notifications ?? false}
          disabled={!messenger && !form.messenger_notifications}
          onChange={(e) => set('messenger_notifications', e.target.checked)}
        />
        <PlanLock feature="messenger" />
      </section>

      {/* Hláška aj pri tlačidle: formulár je dlhý a tá hore je mimo obrazovky. */}
      {message ? <Alert variant={message.kind}>{message.text}</Alert> : null}

      <button type="submit" className="btn-primary" disabled={save.isPending}>
        {save.isPending ? <Spinner /> : null}
        {t('global.save')}
      </button>
    </form>
  )
}

/**
 * Jazyky, v ktorých škola učí.
 *
 * Jedna škola učí po slovensky a anglicky, iná česky, poľsky a rusky —
 * a ponuka v kalendári aj vo widgete má obsahovať to, čo naozaj vie ponúknuť.
 *
 * Ukladá sa **hneď po prepnutí**, nie až tlačidlom dole: je to iná tabuľka než
 * nastavenia a spoločné tlačidlo by sľubovalo, že sa to uloží spolu. Vypnutý
 * jazyk zo systému nemizne — staré lekcie ho majú zapísaný a zoznamy ho podľa
 * číselníka naďalej pomenujú.
 */
function LanguagesSection() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const { data: rows } = useQuery({ queryKey: ['languages'], queryFn: languagesApi.list })

  const toggle = useMutation({
    mutationFn: ({ id, use }: { id: number; use: boolean }) => languagesApi.setUse(id, use),
    onSuccess: () => {
      setError(null)
      void qc.invalidateQueries({ queryKey: ['languages'] })
      // Číselníky nesú ponuku jazykov do celej aplikácie.
      void qc.invalidateQueries({ queryKey: ['bootstrap'] })
      void qc.invalidateQueries({ queryKey: ['calendar'] })
    },
    onError: (err) => setError(apiErrorMessage(err)),
  })

  const sorted = [...(rows ?? [])].sort(languageSort)
  const selected = new Set(sorted.filter((row) => row.use !== false).map((row) => row.id))

  return (
    <section className="card space-y-2 p-4">
      <h2 className="text-sm font-semibold">{t('settings.languages')}</h2>
      <p className="text-xs text-slate-500">{t('settings.languagesHint')}</p>

      {error ? <Alert variant="error">{error}</Alert> : null}

      <MultiSelect
        className="max-w-md pt-1"
        placeholder={t('settings.languagesPlaceholder')}
        disabled={toggle.isPending}
        options={sorted.map((row) => ({
          id: row.id,
          label: row.key ? `${row.key} — ${languageName(t, row.key, row.name)}` : row.name ?? '',
          color: row.color ?? undefined,
        }))}
        selected={selected}
        onToggle={(id) =>
          toggle.mutate({ id: id as number, use: !selected.has(id as number) })
        }
      />
    </section>
  )
}


/** „€ EUR“, „Kč CZK“ - symbol podľa ISO kódu. Kód, ktorý `Intl` nepozná
 *  (vlastný riadok v starom číselníku), ostane bez symbolu. */
function currencyLabel(code: string | null) {
  if (!code) return ''
  try {
    const symbol = new Intl.NumberFormat(getActiveLocale(), {
      style: 'currency',
      currency: code.toUpperCase(),
      // „Kč“, „Ft“, „zł“, „$“ - bežný symbol by v slovenčine pre väčšinu
      // mien vrátil len kód.
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value
    return symbol && symbol !== code.toUpperCase() ? `${symbol} ${code}` : code
  } catch {
    return code
  }
}
